/**
 * Buttons, ported from the prototype's `.btn` family. Variants and sizes are exactly the ones the
 * prototype defines: primary (red), dark, secondary (outlined), ghost — there is no danger
 * variant there, and destructive actions reuse primary, which is already red.
 */
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export type ButtonVariant = 'primary' | 'dark' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  dark: 'btn-dark',
  secondary: 'btn-secondary',
  ghost: 'btn-ghost',
};

const SIZE_CLASS: Record<ButtonSize, string> = { sm: 'btn-sm', md: '', lg: 'btn-lg' };

function classes(
  variant: ButtonVariant,
  size: ButtonSize,
  block: boolean | undefined,
  extra: string | undefined,
): string {
  return ['btn', VARIANT_CLASS[variant], SIZE_CLASS[size], block ? 'btn-block' : '', extra ?? '']
    .filter(Boolean)
    .join(' ');
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  icon?: IconName;
  /** Shows a spinner and blocks further clicks; the backend's idempotency remains the real guard. */
  busy?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  block,
  icon,
  busy = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps): ReactElement {
  return (
    <button
      type={type}
      className={classes(variant, size, block, className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className="spinner" aria-hidden="true" /> : icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
}

export interface LinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  icon?: IconName;
  /** Renders the external-link glyph and the safe rel/target pair. */
  external?: boolean;
  children?: ReactNode;
}

export function LinkButton({
  variant = 'secondary',
  size = 'md',
  block,
  icon,
  external,
  className,
  children,
  ...rest
}: LinkButtonProps): ReactElement {
  return (
    <a
      className={classes(variant, size, block, className)}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...rest}
    >
      {icon ? <Icon name={icon} /> : null}
      {children}
      {external ? <Icon name="ext" /> : null}
    </a>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /** Required: an icon-only control must still say what it does. */
  label: string;
  size?: 'xs' | 'md';
}

export function IconButton({
  icon,
  label,
  size = 'md',
  className,
  type = 'button',
  ...rest
}: IconButtonProps): ReactElement {
  return (
    <button
      type={type}
      className={['icon-btn', size === 'xs' ? 'xs' : '', className ?? ''].filter(Boolean).join(' ')}
      title={label}
      aria-label={label}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  );
}
